import type { DemoAppMessageEvent } from "@/lib/types/demo-session";

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object";
}

function isIsoDate(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function isResumeReason(value: unknown): value is "manual" | "auto" {
  return value === "manual" || value === "auto";
}

function stringOr(value: unknown, fallback: string): string {
  return typeof value === "string" ? value : fallback;
}

/** Trust boundary: engine app-messages arrive as `unknown`; malformed ones are dropped. */
export function parseDemoAppMessage(raw: unknown): DemoAppMessageEvent | null {
  if (!isRecord(raw)) return null;

  switch (raw.event) {
    case "session_ending":
      return {
        event: "session_ending",
        ...(isIsoDate(raw.deadline_at) ? { deadline_at: raw.deadline_at } : {}),
        reason: stringOr(raw.reason, "deadline"),
      };
    case "session_ended":
      return { event: "session_ended", reason: stringOr(raw.reason, "deadline") };
    case "deadline_updated":
      return isIsoDate(raw.deadline_at)
        ? {
            event: "deadline_updated",
            deadline_at: raw.deadline_at,
            ...(raw.reason === "auto_extend" ? { reason: "auto_extend" as const } : {}),
          }
        : null;
    case "session_paused":
      return isIsoDate(raw.paused_at) && isIsoDate(raw.auto_resume_at)
        ? {
            event: "session_paused",
            paused_at: raw.paused_at,
            auto_resume_at: raw.auto_resume_at,
          }
        : null;
    case "session_resumed":
      return isIsoDate(raw.deadline_at) && isResumeReason(raw.reason)
        ? { event: "session_resumed", deadline_at: raw.deadline_at, reason: raw.reason }
        : null;
    case "coach_freeze_started":
      return isIsoDate(raw.paused_at)
        ? {
            event: "coach_freeze_started",
            paused_at: raw.paused_at,
            coach_line: stringOr(raw.coach_line, ""),
            beat_key: stringOr(raw.beat_key, ""),
          }
        : null;
    case "coach_freeze_ended":
      return isIsoDate(raw.deadline_at) && isResumeReason(raw.reason)
        ? { event: "coach_freeze_ended", deadline_at: raw.deadline_at, reason: raw.reason }
        : null;
    default:
      return null;
  }
}
